import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import API, { resolvePhoto } from '../../utils/api';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { id } from 'date-fns/locale';
import { AlignLeft, AlignCenter, AlignRight, AlignJustify, Highlighter, Eraser, Table as TableIcon } from 'lucide-react';
import './EditorArticle.css';

const STATUS_LABELS = {
  writing: 'Sedang Ditulis',
  revision_editor: 'Perlu Revisi',
  review_producer: 'Menunggu Producer',
  review_reader: 'Menunggu Reader',
  revision_reader: 'Revisi dari Pengaju',
  approved: 'Disetujui Reader',
  published: 'Terpublikasi',
};

export default function EditorArticle() {
  const { id: articleId } = useParams();
  const navigate = useNavigate();

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('write');
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [tableHover, setTableHover] = useState({ r: 0, c: 0 });
  const [showDeleteDraft, setShowDeleteDraft] = useState(false);
  const [deletingDraft, setDeletingDraft] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [draft, setDraft] = useState({ title: '', content: '', excerpt: '', seoTitle: '', seoDescription: '', seoKeywords: '', focusKeyword: '' });
  const [photos, setPhotos] = useState([]);
  const [featuredPhoto, setFeaturedPhoto] = useState(0);
  const [revisionNotes, setRevisionNotes] = useState([]);
  const [uploadingInline, setUploadingInline] = useState(false);
  const [uploadingTabPhoto, setUploadingTabPhoto] = useState(false);
  const tabPhotoInputRef = useRef(null);
  const contentRef = useRef(null);
  const inlineInputRef = useRef(null);
  const savedRangeRef = useRef(null);
  const lastClickedImageRef = useRef(null);

  useEffect(() => { fetchArticle(); }, [articleId]);

  // Set HTML ke contentEditable setelah artikel dimuat
  useEffect(() => {
    if (contentRef.current && draft.content !== undefined) {
      if (contentRef.current.innerHTML !== draft.content) {
        contentRef.current.innerHTML = draft.content;
      }
    }
  }, [article]); // hanya saat artikel pertama kali dimuat

  const applyArticle = (found) => {
    setArticle(found);
    const le = found.liveEdit;
    const inLiveEdit = le?.inProgress && le?.stage === 'editing';
    setDraft({
      title: (inLiveEdit ? le.pendingTitle : found.title) || found.what || '',
      content: (inLiveEdit ? le.pendingContent : found.content) || '',
      excerpt: (inLiveEdit ? le.pendingExcerpt : found.excerpt) || '',
      seoTitle: found.seoTitle || '',
      seoDescription: found.seoDescription || '',
      seoKeywords: found.seoKeywords || '',
      focusKeyword: found.focusKeyword || '',
    });
    setPhotos(found.photos || []);
    setFeaturedPhoto(found.featuredPhoto || 0);
    if (found.revisions?.length) {
      setRevisionNotes(found.revisions.filter(r => r.from === 'producer' || r.from === 'reader'));
    }
  };

  const fetchArticle = async () => {
    setLoading(true);

    // Coba dari daftar assigned dulu (paling cepat, sudah populate lengkap)
    try {
      const res = await API.get('/articles/editor/assigned');
      const found = (res.data.articles || []).find(a => a._id === articleId);
      if (found) { applyArticle(found); setLoading(false); return; }
    } catch {}

    // Fallback: fetch langsung by ID (untuk artikel published/seed tanpa assignedEditor)
    try {
      const res = await API.get(`/articles/editor/${articleId}`);
      if (res.data.article) { applyArticle(res.data.article); setLoading(false); return; }
    } catch {}

    // Tidak ditemukan di mana-mana
    toast.error('Artikel tidak ditemukan');
    navigate('/editor');
    setLoading(false);
  };

  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      const liveEditing = article?.liveEdit?.inProgress && article?.liveEdit?.stage === 'editing';
      if (liveEditing) {
        await API.patch(`/articles/editor/${articleId}/save-live-edit`, draft);
        toast.success('Perubahan tersimpan (versi lama masih tayang)');
      } else {
        await API.patch(`/articles/editor/${articleId}/draft`, draft);
        toast.success('Draft tersimpan');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitLiveEdit = async () => {
    if (!draft.title.trim() || !draft.content.trim()) { toast.error('Judul dan konten wajib diisi'); return; }
    try {
      setSubmitting(true);
      await API.patch(`/articles/editor/${articleId}/save-live-edit`, draft);
      await API.patch(`/articles/editor/${articleId}/submit-live-edit`);
      toast.success('Perubahan dikirim ke Producer untuk diverifikasi');
      navigate('/editor');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal mengirim');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateAI = async () => {
    try {
      setAiGenerating(true);
      const res = await API.post('/ai/generate', {
        articleId,
        what: article.what, who: article.who, when: article.when,
        where: article.where, why: article.why, how: article.how,
        additionalContext: article.additionalContext,
        category: article.category?.name,
      });
      const { title, content, excerpt } = res.data.data;
      setDraft({ title, content, excerpt });
      setShowAiPanel(false);
      toast.success('Konten berhasil di-generate. Silakan edit sesuai kebutuhan.');
    } catch {
      toast.error('Gagal generate konten AI');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSubmitToProducer = async () => {
    if (!draft.title.trim() || !draft.content.trim()) {
      toast.error('Judul dan konten wajib diisi');
      return;
    }
    try {
      setSubmitting(true);
      await API.patch(`/articles/editor/${articleId}/draft`, draft);
      const endpoint = isEditorOwned
        ? `/articles/editor/${articleId}/submit-producer-owned`
        : `/articles/editor/${articleId}/submit-producer`;
      await API.patch(endpoint);
      toast.success('Artikel berhasil dikirim ke Producer');
      navigate('/editor');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal mengirim ke Producer');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublishOwned = async () => {
    try {
      setPublishing(true);
      await API.patch(`/articles/editor/${articleId}/publish`);
      toast.success('Artikel berhasil dipublikasikan!');
      navigate('/editor');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal mempublikasikan');
    } finally {
      setPublishing(false);
    }
  };

  const handleDeleteDraft = async () => {
    setDeletingDraft(true);
    try {
      const res = await API.delete(`/articles/editor/${articleId}/delete-draft`);
      toast.success(res.data.returned ? 'Artikel dikembalikan ke antrian editor' : 'Draft berhasil dihapus');
      navigate('/editor');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal menghapus draft');
    } finally {
      setDeletingDraft(false);
      setShowDeleteDraft(false);
    }
  };

  const movePhoto = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= photos.length) return;
    const next = [...photos];
    [next[i], next[j]] = [next[j], next[i]];
    setPhotos(next);
    if (featuredPhoto === i) setFeaturedPhoto(j);
    else if (featuredPhoto === j) setFeaturedPhoto(i);
  };

  const setFeatured = (i) => setFeaturedPhoto(i);

  const updatePhotoCaption = (i, caption) => {
    setPhotos(prev => prev.map((p, idx) => idx === i ? { ...p, caption } : p));
  };

  const uploadTabPhoto = async (files) => {
    if (!files?.length) return;
    if (photos.length + files.length > 5) { toast.error('Maksimal 5 foto'); return; }
    setUploadingTabPhoto(true);
    try {
      const newPhotos = [];
      for (const file of Array.from(files)) {
        if (file.size > 15 * 1024 * 1024) { toast.error(`${file.name}: maks 15MB`); continue; }
        const fd = new FormData();
        fd.append('photo', file);
        const res = await API.post('/upload/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        newPhotos.push({ url: res.data.url, caption: '' });
      }
      if (newPhotos.length) {
        const updated = [...photos, ...newPhotos];
        setPhotos(updated);
        await API.patch(`/articles/editor/${articleId}/draft`, { photos: updated, featuredPhoto });
        toast.success(`${newPhotos.length} foto ditambahkan`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal upload foto');
    } finally {
      setUploadingTabPhoto(false);
      if (tabPhotoInputRef.current) tabPhotoInputRef.current.value = '';
    }
  };

  const insertPhotoToContent = (photo) => {
    // Pastikan tab konten aktif dan editor fokus
    setActiveTab('write');
    setTimeout(() => {
      contentRef.current?.focus();
      const sel = window.getSelection();
      // Pakai savedRange kalau ada, atau akhir konten
      let range;
      if (savedRangeRef.current) {
        range = savedRangeRef.current.cloneRange();
      } else {
        range = document.createRange();
        range.selectNodeContents(contentRef.current);
        range.collapse(false);
      }
      sel.removeAllRanges();
      sel.addRange(range);

      const figure = document.createElement('figure');
      figure.style.cssText = 'margin:24px 0;text-align:center;';
      const img = document.createElement('img');
      img.src = resolvePhoto(photo.url);
      img.alt = photo.caption || '';
      img.style.cssText = 'max-width:100%;height:auto;border-radius:8px;display:inline-block;';
      figure.appendChild(img);
      const cap = document.createElement('figcaption');
      cap.contentEditable = 'true';
      cap.style.cssText = 'font-size:13px;color:#888;margin-top:6px;font-style:italic;';
      cap.textContent = photo.caption || 'Keterangan foto (opsional)';
      figure.appendChild(cap);

      range.deleteContents();
      range.insertNode(figure);
      const afterRange = document.createRange();
      afterRange.setStartAfter(figure);
      afterRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(afterRange);
      savedRangeRef.current = afterRange.cloneRange();
      syncContent();
      toast.success('Foto disisipkan ke konten');
    }, 50);
  };

  const savePhotoOrder = async () => {
    try {
      await API.patch(`/articles/editor/${articleId}/draft`, {
        ...draft,
        featuredPhoto,
        photos,
      });
      toast.success('Urutan foto disimpan');
    } catch {
      toast.error('Gagal menyimpan urutan foto');
    }
  };

  const saveRange = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const insertInlineImage = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Ukuran foto maksimal 15MB');
      return;
    }
    setUploadingInline(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const res = await API.post('/upload/inline', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const url = res.data.url;

      // Restore cursor ke posisi sebelum file picker dibuka
      contentRef.current?.focus();
      const sel = window.getSelection();
      if (savedRangeRef.current) {
        sel.removeAllRanges();
        sel.addRange(savedRangeRef.current);
      }

      // Wrap img dalam figure agar bisa diberi caption
      const figure = document.createElement('figure');
      figure.style.cssText = 'margin:24px 0;text-align:center;';
      const img = document.createElement('img');
      img.src = url;
      img.alt = '';
      img.style.cssText = 'max-width:100%;height:auto;border-radius:8px;display:inline-block;';
      const caption = document.createElement('figcaption');
      caption.contentEditable = 'true';
      caption.style.cssText = 'font-size:13px;color:#888;margin-top:6px;font-style:italic;';
      caption.textContent = 'Keterangan foto (opsional)';
      figure.appendChild(img);
      figure.appendChild(caption);

      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(figure);

      // Pindah kursor setelah figure
      const afterRange = document.createRange();
      afterRange.setStartAfter(figure);
      afterRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(afterRange);

      syncContent();
      toast.success('Foto disisipkan');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal upload foto');
    } finally {
      setUploadingInline(false);
      if (inlineInputRef.current) inlineInputRef.current.value = '';
    }
  };

  const execCmd = (cmd, value = null) => {
    contentRef.current?.focus();
    document.execCommand(cmd, false, value);
    syncContent();
  };

  const syncContent = () => {
    if (contentRef.current) {
      setDraft(d => ({ ...d, content: contentRef.current.innerHTML }));
    }
  };

  const insertBlock = (tag) => {
    contentRef.current?.focus();
    const sel = window.getSelection();
    if (!sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const el = document.createElement(tag);
    el.innerHTML = tag === 'blockquote' ? '<em>Kutipan di sini...</em>' : (tag === 'hr' ? '' : 'Teks di sini...');
    range.insertNode(el);
    const newRange = document.createRange();
    if (tag !== 'hr') {
      newRange.selectNodeContents(el);
      newRange.collapse(false);
      sel.removeAllRanges();
      sel.addRange(newRange);
    }
    syncContent();
  };

  // Warnai sel tabel yang sedang ditempati kursor (atau seluruh tabel terdekat)
  const applyTableCellColor = (color) => {
    const sel = window.getSelection();
    let node = sel?.anchorNode;
    while (node && node !== contentRef.current) {
      if (node.tagName === 'TD' || node.tagName === 'TH') { node.style.background = color; syncContent(); return; }
      if (node.tagName === 'TABLE') {
        node.querySelectorAll('td,th').forEach(c => { c.style.background = color; });
        syncContent(); return;
      }
      node = node.parentNode;
    }
    toast.error('Letakkan kursor di dalam tabel terlebih dahulu.');
  };

  // Atur perataan foto inline yang sedang dipilih (klik gambar dulu)
  const alignSelectedImage = (align) => {
    const sel = window.getSelection();
    let node = sel?.anchorNode;
    // Cari <figure> atau <img> terdekat
    let target = null;
    while (node && node !== contentRef.current) {
      if (node.tagName === 'FIGURE' || node.tagName === 'IMG') { target = node; break; }
      node = node.parentNode;
    }
    if (!target && lastClickedImageRef.current) target = lastClickedImageRef.current.closest('figure') || lastClickedImageRef.current;
    if (!target) { toast.error('Klik foto yang ingin diatur terlebih dahulu.'); return; }
    const fig = target.tagName === 'FIGURE' ? target : target;
    // Batas 50% hanya untuk foto yang belum diatur ukurannya — ukuran pilihan editor tetap dihormati
    const capWidth = fig.style.width ? '100%' : '50%';
    if (align === 'center') { fig.style.textAlign = 'center'; fig.style.margin = '24px auto'; fig.style.float = 'none'; }
    else if (align === 'left') { fig.style.textAlign = 'left'; fig.style.float = 'left'; fig.style.margin = '8px 20px 8px 0'; fig.style.maxWidth = capWidth; }
    else if (align === 'right') { fig.style.textAlign = 'right'; fig.style.float = 'right'; fig.style.margin = '8px 0 8px 20px'; fig.style.maxWidth = capWidth; }
    syncContent();
  };

  // Atur lebar foto inline yang sedang dipilih (klik gambar dulu), dalam persen lebar konten
  const resizeSelectedImage = (pct) => {
    const sel = window.getSelection();
    let node = sel?.anchorNode;
    let target = null;
    while (node && node !== contentRef.current) {
      if (node.tagName === 'FIGURE' || node.tagName === 'IMG') { target = node; break; }
      node = node.parentNode;
    }
    if (!target && lastClickedImageRef.current && contentRef.current?.contains(lastClickedImageRef.current)) {
      target = lastClickedImageRef.current;
    }
    if (!target) { toast.error('Klik foto yang ingin diatur terlebih dahulu.'); return; }

    const img = target.tagName === 'IMG' ? target : target.querySelector('img');
    const fig = target.closest('figure');
    if (fig && contentRef.current.contains(fig)) {
      // Lebar diatur di <figure> agar caption ikut menyesuaikan; foto mengisi figure
      fig.style.width = `${pct}%`;
      fig.style.maxWidth = '100%';
      if (img) img.style.width = '100%';
      // Foto yang tidak mengapung (float) tetap di tengah setelah diperkecil
      if (fig.style.float !== 'left' && fig.style.float !== 'right') {
        fig.style.marginLeft = 'auto';
        fig.style.marginRight = 'auto';
      }
    } else if (img) {
      img.style.width = `${pct}%`;
    }
    syncContent();
  };

  const insertTable = (rows = 3, cols = 3) => {
    contentRef.current?.focus();
    const sel = window.getSelection();
    if (sel && savedRangeRef.current) {
      sel.removeAllRanges();
      sel.addRange(savedRangeRef.current);
    }
    const table = document.createElement('table');
    table.style.cssText = 'width:100%;border-collapse:collapse;margin:20px 0;font-size:14px;';
    for (let r = 0; r < rows; r++) {
      const tr = document.createElement('tr');
      for (let c = 0; c < cols; c++) {
        const cell = r === 0 ? document.createElement('th') : document.createElement('td');
        cell.contentEditable = 'true';
        cell.style.cssText = r === 0
          ? 'border:1px solid #ccc;padding:8px 12px;background:#f0f4ff;font-weight:700;text-align:left;'
          : 'border:1px solid #ccc;padding:8px 12px;text-align:left;';
        cell.textContent = r === 0 ? `Kolom ${c + 1}` : '';
        tr.appendChild(cell);
      }
      table.appendChild(tr);
    }
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'overflow-x:auto;margin:20px 0;';
    wrapper.appendChild(table);
    const range2 = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
    if (range2) {
      range2.deleteContents();
      range2.insertNode(wrapper);
      const after = document.createRange();
      after.setStartAfter(wrapper);
      after.collapse(true);
      sel.removeAllRanges();
      sel.addRange(after);
    } else {
      contentRef.current.appendChild(wrapper);
    }
    syncContent();
  };

  if (loading) return (
    <DashboardLayout>
      <div className="editor-loading">
        <div className="spinner" />
        <p style={{ color: 'var(--ki-text-muted)', marginTop: 12 }}>Memuat artikel...</p>
      </div>
    </DashboardLayout>
  );

  if (!article) return null;

  const isEditorOwned = article.isEditorOwned === true;
  const canPublish = article.status === 'approved';
  // Live-edit: artikel published yang diminta producer untuk diperbarui (tetap tayang)
  const isLiveEditing = article.liveEdit?.inProgress && article.liveEdit?.stage === 'editing';
  const canSubmitProducer = ['writing', 'revision_editor', 'revision_reader'].includes(article.status);
  const isReadOnly = !['writing', 'revision_editor', 'revision_reader'].includes(article.status) && !isLiveEditing;

  const lastRevision = revisionNotes[revisionNotes.length - 1];

  return (
    <DashboardLayout>
      <div className="editor-article-page">

        {/* Header */}
        <div className="editor-header">
          <div className="editor-header-left">
            <button className="back-btn" onClick={() => navigate('/editor')}>
              &larr; Kembali
            </button>
            <span className={`status-chip status-${article.status}`}>
              {STATUS_LABELS[article.status] || article.status}
            </span>
            <span className="article-category">{article.category?.name}</span>
            <span className="article-time">
              Diperbarui {formatDistanceToNow(new Date(article.updatedAt), { addSuffix: true, locale: id })}
            </span>
          </div>
          <div className="editor-header-actions">
            {!isReadOnly && (
              <>
                <button className="btn-ai" onClick={() => setShowAiPanel(!showAiPanel)} disabled={aiGenerating}>
                  AI Generate
                </button>
                <button className="btn-save" onClick={handleSaveDraft} disabled={saving}>
                  {saving ? 'Menyimpan...' : isLiveEditing ? 'Simpan Perubahan' : 'Simpan Draft'}
                </button>
                {!isLiveEditing && (
                  <button
                    onClick={() => setShowDeleteDraft(true)}
                    style={{ padding: '7px 14px', background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)', color: '#DC2626', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}
                    title={article?.isEditorOwned ? 'Hapus draft artikel ini' : 'Kembalikan artikel ke antrian'}
                  >
                    {article?.isEditorOwned ? 'Hapus Draft' : 'Kembalikan ke Antrian'}
                  </button>
                )}
              </>
            )}
            {isLiveEditing && (
              <button className="btn-submit-producer" onClick={handleSubmitLiveEdit} disabled={submitting}>
                {submitting ? 'Mengirim...' : 'Kirim Perubahan ke Producer'}
              </button>
            )}
            {canSubmitProducer && (
              <button className="btn-submit-producer" onClick={handleSubmitToProducer} disabled={submitting}>
                {submitting ? 'Mengirim...' : 'Kirim ke Producer'}
              </button>
            )}
            {canPublish && !isEditorOwned && (
              <span style={{ fontSize: 13, color: 'var(--ki-text-faint)', fontStyle: 'italic', padding: '0 8px' }}>
                Menunggu Producer untuk mempublikasikan
              </span>
            )}
            {canPublish && isEditorOwned && (
              <button className="btn-submit-producer" onClick={handlePublishOwned} disabled={publishing}>
                {publishing ? 'Mempublikasikan...' : 'Publikasikan'}
              </button>
            )}
          </div>
        </div>

        {/* Revision banner */}
        {lastRevision && (
          <div className="revision-banner">
            <div className="revision-banner-icon" />
            <div className="revision-banner-content">
              <div className="revision-banner-title">
                Catatan Revisi dari {lastRevision.from === 'producer' ? 'Producer' : 'Reader'}
              </div>
              <div className="revision-banner-note">{lastRevision.notes}</div>
            </div>
          </div>
        )}

        {/* AI Panel */}
        {showAiPanel && (
          <div className="ai-panel">
            <div className="ai-panel-header">
              <span className="ai-badge">AI Writer</span>
              <button className="ai-close" onClick={() => setShowAiPanel(false)}>X</button>
            </div>
            <p className="ai-desc">
              AI akan membuat artikel lengkap berdasarkan detail yang diisi reader. Hasil dapat diedit secara manual.
            </p>
            <div className="ai-context-preview">
              {[
                ['Apa', article.what], ['Siapa', article.who], ['Kapan', article.when],
                ['Di mana', article.where], ['Mengapa', article.why], ['Bagaimana', article.how],
              ].map(([label, value]) => (
                <div key={label} className="context-row">
                  <label>{label}</label>
                  <span>{value}</span>
                </div>
              ))}
            </div>
            <button className="btn-generate" onClick={handleGenerateAI} disabled={aiGenerating}>
              {aiGenerating ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <span className="spinner-sm" /> Membuat artikel...
                </span>
              ) : 'Generate Artikel dengan AI'}
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="editor-tabs-bar">
          <button className={`etab ${activeTab === 'write' ? 'active' : ''}`} onClick={() => setActiveTab('write')}>
            Tulis
          </button>
          {!isEditorOwned && (
            <button className={`etab ${activeTab === 'brief' ? 'active' : ''}`} onClick={() => setActiveTab('brief')}>
              Brief dari Reader
            </button>
          )}
          <button className={`etab ${activeTab === 'preview' ? 'active' : ''}`} onClick={() => setActiveTab('preview')}>
            Preview
          </button>
          {!isReadOnly && (
            <button className={`etab ${activeTab === 'photos' ? 'active' : ''}`} onClick={() => setActiveTab('photos')}>
              Foto {photos.length > 0 ? `(${photos.length})` : ''}
            </button>
          )}
          <button className={`etab ${activeTab === 'seo' ? 'active' : ''}`} onClick={() => setActiveTab('seo')}>
            SEO
          </button>
        </div>

        {/* Tab Content — semua tab selalu di-render, visibility dikontrol display agar contentEditable tidak di-unmount */}
        <div className="editor-body">

          {/* Write Tab */}
          <div className="write-tab" style={{ display: activeTab === 'write' ? 'block' : 'none' }}>
            <input
              type="text"
              className="input-title"
              value={draft.title}
              onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
              placeholder="Tulis judul artikel yang menarik..."
              disabled={isReadOnly}
            />
            <textarea
              className="input-excerpt"
              rows={2}
              value={draft.excerpt}
              onChange={e => setDraft(d => ({ ...d, excerpt: e.target.value }))}
              placeholder="Ringkasan singkat (1–2 kalimat)..."
              disabled={isReadOnly}
            />
            {!isReadOnly && (
              <div className="editor-toolbar">
                {/* Format teks */}
                {[
                  { label: 'B', title: 'Bold', action: () => execCmd('bold'), style: { fontWeight: 800 } },
                  { label: 'I', title: 'Italic', action: () => execCmd('italic'), style: { fontStyle: 'italic' } },
                  { label: 'U', title: 'Underline', action: () => execCmd('underline'), style: { textDecoration: 'underline' } },
                  { label: 'S', title: 'Strikethrough', action: () => execCmd('strikeThrough'), style: { textDecoration: 'line-through' } },
                ].map(btn => (
                  <button key={btn.label} type="button" className="etb-btn" title={btn.title} onMouseDown={e => { e.preventDefault(); btn.action(); }} style={btn.style}>
                    {btn.label}
                  </button>
                ))}
                <div className="etb-sep" />
                {/* Heading & paragraf */}
                {[
                  { label: 'H1', title: 'Heading 1', action: () => execCmd('formatBlock', 'h1') },
                  { label: 'H2', title: 'Heading 2', action: () => execCmd('formatBlock', 'h2') },
                  { label: 'H3', title: 'Heading 3', action: () => execCmd('formatBlock', 'h3') },
                  { label: 'P',  title: 'Paragraf',  action: () => execCmd('formatBlock', 'p')  },
                ].map(btn => (
                  <button key={btn.label} type="button" className="etb-btn etb-mono" title={btn.title} onMouseDown={e => { e.preventDefault(); btn.action(); }}>
                    {btn.label}
                  </button>
                ))}
                <div className="etb-sep" />
                {/* Alignment — ikon ala docx */}
                {[
                  { icon: <AlignLeft size={15} />,    title: 'Rata kiri',   action: () => execCmd('justifyLeft')   },
                  { icon: <AlignCenter size={15} />,  title: 'Rata tengah', action: () => execCmd('justifyCenter') },
                  { icon: <AlignRight size={15} />,   title: 'Rata kanan',  action: () => execCmd('justifyRight')  },
                  { icon: <AlignJustify size={15} />, title: 'Rata penuh',  action: () => execCmd('justifyFull')   },
                ].map((btn, i) => (
                  <button key={i} type="button" className="etb-btn" title={btn.title} onMouseDown={e => { e.preventDefault(); btn.action(); }}>
                    {btn.icon}
                  </button>
                ))}
                <div className="etb-sep" />
                {/* List & blok */}
                {[
                  { label: '• List', title: 'Bullet list',     action: () => execCmd('insertUnorderedList') },
                  { label: '1. List', title: 'Numbered list',  action: () => execCmd('insertOrderedList')   },
                  { label: 'Quote',   title: 'Blockquote',     action: () => insertBlock('blockquote')      },
                  { label: '—',       title: 'Garis pembatas', action: () => insertBlock('hr')              },
                ].map(btn => (
                  <button key={btn.label} type="button" className="etb-btn" title={btn.title} onMouseDown={e => { e.preventDefault(); btn.action(); }}>
                    {btn.label}
                  </button>
                ))}
                <div className="etb-sep" />
                {/* Indentasi */}
                {[
                  { label: '→ Indent',  title: 'Tambah indentasi', action: () => execCmd('indent')  },
                  { label: '← Outdent', title: 'Kurangi indentasi', action: () => execCmd('outdent') },
                ].map(btn => (
                  <button key={btn.label} type="button" className="etb-btn" title={btn.title} onMouseDown={e => { e.preventDefault(); btn.action(); }}>
                    {btn.label}
                  </button>
                ))}
                <div className="etb-sep" />
                {/* Link */}
                <button type="button" className="etb-btn" title="Sisipkan link" onMouseDown={e => {
                    e.preventDefault();
                    const url = window.prompt('Masukkan URL:');
                    if (url) execCmd('createLink', url);
                  }}>
                  Link
                </button>
                <button type="button" className="etb-btn" title="Hapus link" onMouseDown={e => { e.preventDefault(); execCmd('unlink'); }}>
                  Hapus Link
                </button>
                <div className="etb-sep" />
                {/* Undo Redo */}
                {[
                  { label: '↩ Undo', title: 'Undo', action: () => execCmd('undo') },
                  { label: '↪ Redo', title: 'Redo', action: () => execCmd('redo') },
                ].map(btn => (
                  <button key={btn.label} type="button" className="etb-btn" title={btn.title} onMouseDown={e => { e.preventDefault(); btn.action(); }}>
                    {btn.label}
                  </button>
                ))}
                <div className="etb-sep" />
                {/* Warna font */}
                <label className="etb-btn" title="Warna teks">
                  A
                  <input type="color" defaultValue="#000000"
                    style={{ width: 18, height: 18, border: 'none', padding: 0, cursor: 'pointer', borderRadius: 3 }}
                    onChange={e => { contentRef.current?.focus(); execCmd('foreColor', e.target.value); }} />
                </label>
                {/* Sorot / highlight + nonaktifkan */}
                <label className="etb-btn" title="Warna sorot">
                  <Highlighter size={14} />
                  <input type="color" defaultValue="#FFFF00"
                    style={{ width: 18, height: 18, border: 'none', padding: 0, cursor: 'pointer', borderRadius: 3 }}
                    onChange={e => { contentRef.current?.focus(); execCmd('hiliteColor', e.target.value); }} />
                </label>
                <button type="button" className="etb-btn" title="Nonaktifkan sorot"
                  onMouseDown={e => { e.preventDefault(); contentRef.current?.focus(); execCmd('hiliteColor', 'transparent'); }}>
                  <Eraser size={14} />
                </button>
                <div className="etb-sep" />
                {/* Tabel */}
                <div style={{ position: 'relative' }}>
                  <button type="button" className="etb-btn" title="Sisipkan tabel"
                    onMouseDown={e => { e.preventDefault(); saveRange(); setShowTablePicker(p => !p); }}
                    style={showTablePicker ? { background: 'var(--in-bg-4)' } : undefined}>
                    <TableIcon size={14} /> Tabel
                  </button>
                  {showTablePicker && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: 'var(--in-card)', border: '1px solid var(--ki-border)', borderRadius: 8, padding: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', marginTop: 4 }}
                      onMouseLeave={() => setShowTablePicker(false)}>
                      <div style={{ fontSize: 11, color: 'var(--in-text-faint)', marginBottom: 6, whiteSpace: 'nowrap' }}>
                        {tableHover.r > 0 ? `${tableHover.r} × ${tableHover.c} tabel` : 'Pilih ukuran tabel'}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 22px)', gap: 2 }}>
                        {Array.from({ length: 36 }, (_, i) => {
                          const r = Math.floor(i / 6) + 1;
                          const c = (i % 6) + 1;
                          const active = r <= tableHover.r && c <= tableHover.c;
                          return (
                            <div key={i}
                              style={{ width: 20, height: 20, border: `1px solid ${active ? 'var(--in-blue)' : 'var(--ki-border)'}`, background: active ? 'rgba(0,21,64,0.1)' : 'var(--in-bg-3)', borderRadius: 2, cursor: 'pointer' }}
                              onMouseEnter={() => setTableHover({ r, c })}
                              onMouseDown={e => { e.preventDefault(); insertTable(r, c); setShowTablePicker(false); setTableHover({ r: 0, c: 0 }); }} />
                          );
                        })}
                      </div>
                      {/* Warna latar tabel */}
                      <label className="etb-btn" title="Warna latar sel tabel" style={{ marginTop: 8, fontSize: 11 }}>
                        Warna sel
                        <input type="color" defaultValue="#f0f4ff"
                          style={{ width: 18, height: 18, border: 'none', padding: 0, cursor: 'pointer', borderRadius: 3 }}
                          onChange={e => applyTableCellColor(e.target.value)} />
                      </label>
                    </div>
                  )}
                </div>
                <div className="etb-sep" />
                {/* Alignment foto inline (yang sedang dipilih) */}
                {[
                  { icon: <AlignLeft size={14} />, title: 'Foto rata kiri', val: 'left' },
                  { icon: <AlignCenter size={14} />, title: 'Foto rata tengah', val: 'center' },
                  { icon: <AlignRight size={14} />, title: 'Foto rata kanan', val: 'right' },
                ].map(b => (
                  <button key={b.val} type="button" className="etb-btn" title={b.title}
                    onMouseDown={e => { e.preventDefault(); alignSelectedImage(b.val); }}>
                    {b.icon}
                  </button>
                ))}
                {/* Ukuran foto inline (yang sedang dipilih) */}
                {[25, 50, 75, 100].map(pct => (
                  <button key={pct} type="button" className="etb-btn etb-mono" title={`Ukuran foto ${pct}% lebar konten`}
                    onMouseDown={e => { e.preventDefault(); resizeSelectedImage(pct); }}>
                    {pct}%
                  </button>
                ))}
                <div className="etb-sep" />
                {/* Foto inline */}
                <button type="button" className="etb-btn" title="Sisipkan foto" disabled={uploadingInline}
                  onMouseDown={e => { e.preventDefault(); saveRange(); inlineInputRef.current?.click(); }}
                  style={{ background: 'var(--in-blue)', color: '#fff', fontWeight: 600, opacity: uploadingInline ? 0.5 : 1 }}>
                  {uploadingInline ? 'Upload...' : '+ Foto'}
                </button>
                <input ref={inlineInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" style={{ display: 'none' }}
                  onChange={e => { const file = e.target.files?.[0]; if (file) insertInlineImage(file); }} />
              </div>
            )}
            <div
              ref={contentRef}
              className="content-editable"
              contentEditable={!isReadOnly}
              suppressContentEditableWarning
              onInput={syncContent}
              onBlur={() => { saveRange(); syncContent(); }}
              onClick={e => { if (e.target.tagName === 'IMG') lastClickedImageRef.current = e.target; }}
              data-placeholder="Tulis isi artikel di sini — gunakan toolbar untuk heading, kutipan, dan pemformatan..."
              style={{
                minHeight: 480,
                padding: '20px 24px',
                border: '1px solid var(--ki-border)',
                borderTop: isReadOnly ? '1px solid var(--ki-border)' : 'none',
                borderRadius: isReadOnly ? 8 : '0 0 8px 8px',
                background: 'var(--ki-card)',
                outline: 'none',
                fontSize: 15,
                lineHeight: 1.8,
                color: 'var(--ki-text)',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                overflowY: 'auto',
              }}
            />
            <div style={{ fontSize: 12, color: 'var(--ki-text-faint)', textAlign: 'right', marginTop: 6 }}>
              {draft.content.replace(/<[^>]*>/g, ' ').trim().split(/\s+/).filter(Boolean).length} kata
            </div>
          </div>

          {/* Brief Tab */}
          <div className="brief-tab" style={{ display: activeTab === 'brief' ? 'block' : 'none' }}>
            <div className="brief-grid">
              {[
                { label: 'Apa (What)', value: article.what },
                { label: 'Siapa (Who)', value: article.who },
                { label: 'Kapan (When)', value: article.when },
                { label: 'Di mana (Where)', value: article.where },
                { label: 'Mengapa (Why)', value: article.why },
                { label: 'Bagaimana (How)', value: article.how },
              ].map(({ label, value }) => (
                <div key={label} className="brief-field">
                  <div className="brief-label">{label}</div>
                  <div className="brief-val">{value || <em style={{ opacity: 0.5 }}>Tidak diisi</em>}</div>
                </div>
              ))}
            </div>
            {article.additionalContext && (
              <div className="brief-field" style={{ gridColumn: 'span 2' }}>
                <div className="brief-label">Konteks Tambahan</div>
                <div className="brief-val">{article.additionalContext}</div>
              </div>
            )}
            {revisionNotes.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <div className="section-title">Riwayat Revisi</div>
                {revisionNotes.map((rev, i) => (
                  <div key={i} style={{ padding: '12px 16px', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--ki-border)', borderRadius: 8, marginBottom: 10 }}>
                    <div style={{ fontSize: 12, color: 'var(--ki-text-faint)', marginBottom: 6 }}>
                      Dari: <strong style={{ textTransform: 'capitalize', color: 'var(--ki-text-muted)' }}>{rev.from}</strong>
                      {', '}{formatDistanceToNow(new Date(rev.createdAt), { addSuffix: true, locale: id })}
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--ki-text)' }}>{rev.notes}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Preview Tab */}
          <div className="preview-tab" style={{ display: activeTab === 'preview' ? 'block' : 'none' }}>
            <div style={{ background: 'var(--ki-card)', border: '1px solid var(--ki-border)', borderRadius: 16, padding: 32 }}>
              {article.category?.name && (
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: article.category.color || 'var(--ki-violet)', letterSpacing: 1, marginBottom: 12 }}>
                  {article.category.name}
                </div>
              )}
              <h1 className="preview-title">{draft.title || 'Judul belum diisi'}</h1>
              {draft.excerpt && <p className="preview-excerpt">{draft.excerpt}</p>}
              {photos.length > 0 && (
                <img
                  src={resolvePhoto(photos[featuredPhoto]?.url || photos[0]?.url)}
                  alt="Cover"
                  style={{ width: '100%', maxHeight: 360, objectFit: 'cover', borderRadius: 12, marginBottom: 24 }}
                />
              )}
              <div className="article-body" dangerouslySetInnerHTML={{ __html: draft.content.replace(/\n/g, '<br/>') }} />
            </div>
          </div>

          {/* SEO Tab */}
          <div className="seo-tab" style={{ display: activeTab === 'seo' ? 'block' : 'none' }}>
            <div style={{ background: 'var(--ki-card)', border: '1px solid var(--ki-border)', borderRadius: 16, padding: 28, maxWidth: 640 }}>
              <p style={{ fontSize: 13, color: 'var(--ki-text-muted)', marginBottom: 20, lineHeight: 1.6 }}>
                Atur judul, deskripsi, dan kata kunci khusus agar artikel ini lebih mudah ditemukan di Google. Kosongkan untuk memakai judul/ringkasan artikel secara otomatis.
              </p>

              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--ki-text-muted)', display: 'block', marginBottom: 6 }}>
                Meta Judul (SEO Title)
              </label>
              <input
                type="text"
                className="input-title"
                style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}
                value={draft.seoTitle}
                onChange={e => setDraft(d => ({ ...d, seoTitle: e.target.value }))}
                placeholder={draft.title || 'Judul artikel akan dipakai jika kosong'}
                disabled={isReadOnly}
                maxLength={70}
              />
              <div style={{ fontSize: 11, color: 'var(--ki-text-faint)', marginBottom: 18, textAlign: 'right' }}>
                {(draft.seoTitle || '').length}/70 karakter (ideal 50–60)
              </div>

              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--ki-text-muted)', display: 'block', marginBottom: 6 }}>
                Meta Deskripsi (SEO Description)
              </label>
              <textarea
                className="input-excerpt"
                rows={3}
                style={{ marginBottom: 4 }}
                value={draft.seoDescription}
                onChange={e => setDraft(d => ({ ...d, seoDescription: e.target.value }))}
                placeholder={draft.excerpt || 'Ringkasan artikel akan dipakai jika kosong'}
                disabled={isReadOnly}
                maxLength={160}
              />
              <div style={{ fontSize: 11, color: 'var(--ki-text-faint)', marginBottom: 18, textAlign: 'right' }}>
                {(draft.seoDescription || '').length}/160 karakter (ideal 120–155)
              </div>

              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--ki-text-muted)', display: 'block', marginBottom: 6 }}>
                Kata Kunci Utama (Focus Keyword)
              </label>
              <input
                type="text"
                className="input-title"
                style={{ fontSize: 14, marginBottom: 18 }}
                value={draft.focusKeyword}
                onChange={e => setDraft(d => ({ ...d, focusKeyword: e.target.value }))}
                placeholder="Contoh: beasiswa riset kampus"
                disabled={isReadOnly}
              />

              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--ki-text-muted)', display: 'block', marginBottom: 6 }}>
                Kata Kunci Tambahan (Keywords)
              </label>
              <input
                type="text"
                className="input-title"
                style={{ fontSize: 14, marginBottom: 24 }}
                value={draft.seoKeywords}
                onChange={e => setDraft(d => ({ ...d, seoKeywords: e.target.value }))}
                placeholder="Pisahkan dengan koma, contoh: riset, kampus, inovasi"
                disabled={isReadOnly}
              />

              {/* Preview snippet ala hasil pencarian Google */}
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ki-text-muted)', marginBottom: 8 }}>Pratinjau di Google</div>
              <div style={{ border: '1px solid var(--ki-border)', borderRadius: 10, padding: '14px 16px', background: 'var(--ki-bg)' }}>
                <div style={{ fontSize: 13, color: '#1a0dab', marginBottom: 3, fontFamily: 'arial,sans-serif' }}>
                  {(draft.seoTitle || draft.title || 'Judul Artikel')}
                </div>
                <div style={{ fontSize: 12, color: '#006621', marginBottom: 4, fontFamily: 'arial,sans-serif' }}>
                  inspirasiya.com › berita › ...
                </div>
                <div style={{ fontSize: 12.5, color: '#545454', lineHeight: 1.5, fontFamily: 'arial,sans-serif' }}>
                  {(draft.seoDescription || draft.excerpt || 'Deskripsi artikel akan tampil di sini.')}
                </div>
              </div>
            </div>
          </div>

          {/* Photos Tab */}
          <div className="photos-tab" style={{ display: activeTab === 'photos' ? 'block' : 'none' }}>
            {/* Tombol tambah foto — selalu tampil selama belum 5 foto */}
            {photos.length < 5 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                  padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  background: 'var(--ki-purple)', color: '#fff', opacity: uploadingTabPhoto ? 0.6 : 1,
                  pointerEvents: uploadingTabPhoto ? 'none' : 'auto'
                }}>
                  {uploadingTabPhoto ? 'Mengupload...' : '+ Tambah Foto'}
                  <input
                    ref={tabPhotoInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    multiple
                    style={{ display: 'none' }}
                    onChange={e => uploadTabPhoto(e.target.files)}
                  />
                </label>
                <span style={{ fontSize: 12, color: 'var(--ki-text-faint)', marginLeft: 10 }}>
                  Maks 5 foto · 15MB per foto · {5 - photos.length} slot tersisa
                </span>
              </div>
            )}
            {photos.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', color: 'var(--ki-text-faint)', fontSize: 14, border: '1px dashed var(--ki-border)', borderRadius: 10 }}>
                Belum ada foto. Upload foto di atas atau minta pengaju mengirim ulang dengan foto.
              </div>
            ) : (
              <>
                <div style={{ fontSize: 13, color: 'var(--ki-text-muted)', marginBottom: 16 }}>
                  Klik <strong>Jadikan Utama</strong> untuk memilih foto cover. Klik <strong>Sisipkan ke Konten</strong> untuk memasukkan foto ke dalam artikel.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {photos.map((photo, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      padding: 12, borderRadius: 10,
                      border: `1px solid ${i === featuredPhoto ? 'var(--ki-purple)' : 'var(--ki-border)'}`,
                      background: i === featuredPhoto ? 'rgba(0,21,64,0.08)' : 'var(--ki-card)',
                    }}>
                      <img src={resolvePhoto(photo.url)} alt={`Foto ${i + 1}`}
                        style={{ width: 100, height: 70, objectFit: 'cover', borderRadius: 7, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ki-text)', marginBottom: 4 }}>
                          Foto {i + 1}
                          {i === featuredPhoto && (
                            <span style={{ marginLeft: 8, fontSize: 11, background: 'var(--ki-purple)', color: '#fff', padding: '2px 8px', borderRadius: 20, fontWeight: 700 }}>
                              Utama
                            </span>
                          )}
                        </div>
                        <input
                          type="text"
                          className="form-input"
                          value={photo.caption || ''}
                          onChange={e => updatePhotoCaption(i, e.target.value)}
                          placeholder="Keterangan foto (opsional)"
                          style={{ fontSize: 12, padding: '5px 8px', marginTop: 2 }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                        <button onClick={() => movePhoto(i, -1)} disabled={i === 0}
                          style={{ padding: '4px 10px', fontSize: 13, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--ki-border)', borderRadius: 5, color: 'var(--ki-text-muted)', cursor: i === 0 ? 'not-allowed' : 'pointer', opacity: i === 0 ? 0.4 : 1 }}>
                          Naik
                        </button>
                        <button onClick={() => movePhoto(i, 1)} disabled={i === photos.length - 1}
                          style={{ padding: '4px 10px', fontSize: 13, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--ki-border)', borderRadius: 5, color: 'var(--ki-text-muted)', cursor: i === photos.length - 1 ? 'not-allowed' : 'pointer', opacity: i === photos.length - 1 ? 0.4 : 1 }}>
                          Turun
                        </button>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                        {i !== featuredPhoto && (
                          <button onClick={() => setFeatured(i)} className="btn btn-primary btn-sm">
                            Jadikan Utama
                          </button>
                        )}
                        <button onClick={() => insertPhotoToContent(photo)} className="btn btn-secondary btn-sm">
                          Sisipkan ke Konten
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={savePhotoOrder} className="btn btn-primary btn-sm">
                    Simpan Urutan Foto
                  </button>
                </div>
              </>
            )}
          </div>

        </div>
      </div>

      {showDeleteDraft && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'var(--ki-card)', borderRadius: 14, width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '20px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 800 }}>
                {article?.isEditorOwned ? 'Hapus Draft Artikel' : 'Kembalikan ke Antrian'}
              </div>
              <button onClick={() => setShowDeleteDraft(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ki-text-faint)', fontSize: 20, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '16px 24px 20px' }}>
              {article?.isEditorOwned ? (
                <p style={{ fontSize: 14, color: 'var(--ki-text-muted)', marginBottom: 16 }}>
                  Draft artikel ini akan dihapus secara permanen. Tindakan tidak dapat dibatalkan.
                </p>
              ) : (
                <p style={{ fontSize: 14, color: 'var(--ki-text-muted)', marginBottom: 16 }}>
                  Artikel akan dikembalikan ke antrian sehingga editor lain dapat mengambilnya.
                  <strong style={{ display: 'block', marginTop: 8, color: 'var(--ki-text)' }}>Seluruh editor akan dinotifikasi via email.</strong>
                </p>
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowDeleteDraft(false)} disabled={deletingDraft} style={{ padding: '7px 16px', background: 'none', border: '1px solid var(--ki-border)', borderRadius: 7, cursor: 'pointer', color: 'var(--ki-text-muted)', fontFamily: 'inherit', fontSize: 13 }}>
                  Batal
                </button>
                <button onClick={handleDeleteDraft} disabled={deletingDraft}
                  style={{ padding: '7px 16px', background: '#DC2626', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, cursor: deletingDraft ? 'not-allowed' : 'pointer', opacity: deletingDraft ? 0.6 : 1, fontFamily: 'inherit', fontSize: 13 }}>
                  {deletingDraft ? 'Memproses...' : article?.isEditorOwned ? 'Ya, Hapus' : 'Ya, Kembalikan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
